import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import ProfileSidebar from '../components/profile/ProfileSidebar';
import { getUserSession } from '../services/authService';
import { changeUserPassword } from '../services/passwordChangeService';
import { getPasswordRules, isPasswordValid } from '../services/passwordRecoveryService';
import '../styles/profile.css';
import '../styles/auth.css';

const LockIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 118 0v3" />
  </svg>
);

const EyeIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const RuleIcon = ({ met }) => (
  <span className={`recovery-rule-icon${met ? ' recovery-rule-icon--met' : ''}`} aria-hidden="true">
    {met ? '✓' : '✕'}
  </span>
);

const passwordRuleItems = [
  { id: 'minLength', label: 'Longitud Mínima: Al menos 12 caracteres.' },
  { id: 'mixedCase', label: 'Letras mayúsculas y minúsculas (Ejemplo: AaBb).' },
  { id: 'numbers', label: 'Números (Ejemplo: 123).' },
  { id: 'symbols', label: 'Símbolos especiales (Ejemplo: *@#!?)' },
];

const ProfileChangePasswordPage = () => {
  const sessionUser = getUserSession();
  const sessionUserId = sessionUser ? sessionUser.id : null;
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordRules = getPasswordRules(password);

  if (!sessionUser) {
    return <Redirect to="/iniciar-sesion" />;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!currentPassword.trim()) {
      setErrorMessage('La contraseña actual es obligatoria.');
      return;
    }

    if (!isPasswordValid(password)) {
      setErrorMessage('La contraseña no cumple con los requisitos.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Las contraseñas deben coincidir.');
      return;
    }

    setIsSubmitting(true);

    changeUserPassword(sessionUserId, {
      passwordActual: currentPassword,
      password,
      confirmPassword,
    })
      .then(() => {
        setSuccessMessage('Contraseña actualizada correctamente.');
        setCurrentPassword('');
        setPassword('');
        setConfirmPassword('');
      })
      .catch((error) => {
        setErrorMessage(error.message || 'No se pudo actualizar la contraseña.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="profile-root">
      <div className="profile-layout">
        <ProfileSidebar activeId="password" />

        <section className="profile-main">
          <form className="profile-password-form" onSubmit={handleSubmit}>
            <label className="auth-field">
              <span className="auth-field-control">
                <LockIcon />
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="Contraseña actual"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowCurrentPassword((value) => !value)}
                  aria-label="Mostrar u ocultar contraseña actual"
                >
                  <EyeIcon />
                </button>
              </span>
            </label>

            <label className="auth-field">
              <span className="auth-field-control">
                <LockIcon />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Contraseña"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label="Mostrar u ocultar contraseña"
                >
                  <EyeIcon />
                </button>
              </span>
            </label>

            <label className="auth-field">
              <span className="auth-field-control">
                <LockIcon />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirmar contraseña"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  aria-label="Mostrar u ocultar confirmación"
                >
                  <EyeIcon />
                </button>
              </span>
            </label>

            <ul className="recovery-rules">
              {passwordRuleItems.map((rule) => (
                <li
                  key={rule.id}
                  className={`recovery-rule${passwordRules[rule.id] ? ' recovery-rule--met' : ''}`}
                >
                  <RuleIcon met={passwordRules[rule.id]} />
                  <span>{rule.label}</span>
                </li>
              ))}
            </ul>

            {errorMessage ? <p className="register-error">{errorMessage}</p> : null}
            {successMessage ? <p className="profile-status">{successMessage}</p> : null}

            <div className="profile-password-actions">
              <button type="submit" className="profile-password-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Actualizando...' : 'Actualizar contraseña >'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ProfileChangePasswordPage;
